import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import {
  AgreementRefreshedDocument,
  PerformanceProcessAgreementDocumentsDocument,
  PerformanceProcessAgreementDocumentsEsignaturesDocument,
  PerformanceProcessAgreementPageDocument,
  PerformanceProcessAgreementQuestionnairesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage, ResponsivePageBanner } from 'modules/layout/components/ResponsivePage'
import { ActionItemList } from 'modules/performance_process/components/agreements/ActionItemList'
import { AgreementCourse } from 'modules/performance_process/components/agreements/AgreementCourse'
import { AgreementConclusions } from 'modules/performance_process/components/agreements/Conclusions'
import { SectionableQuestions } from 'modules/performance_process/components/agreements/SectionableQuestions'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

import { DocumentCard } from './DocumentCard'
import { useBuildTransactionalActions } from './transactionalActions'
import { useStatusAlert } from './useStatusAlert'

const ReviewAgreementPage = () => {
  const { agreementId } = useParams()

  if (!agreementId) {
    throw new Error('Agreement ID is required')
  }

  const { access: currentAccess } = useCurrentSession()
  const { isAgreementDocumentEnabled } = useNewPerformanceModuleContext()

  const { data, loading, error, refetch } = useQuery(PerformanceProcessAgreementPageDocument, {
    variables: { id: agreementId },
  })

  const {
    data: questionnairesData,
    loading: questionnairesLoading,
    error: questionnairesError,
    refetch: refetchQuestionnaires,
  } = useQuery(PerformanceProcessAgreementQuestionnairesDocument, {
    variables: { id: agreementId },
  })

  const {
    data: documentsData,
    loading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments,
  } = useQuery(PerformanceProcessAgreementDocumentsDocument, {
    variables: { id: agreementId },
  })

  const agreement = data?.performance?.agreement
  const agreementWithQuestionnaires = questionnairesData?.performance?.agreement
  const agreementWithDocuments = documentsData?.performance?.agreement

  const document = agreementWithDocuments?.agreementDocuments[0]?.document
  const actionItems = agreement?.actionItemsInfo ?? []
  const canSeeActionItems = agreement?._policies?.addActionItems.can || actionItems.length > 0

  const { data: esignaturesData, loading: esignaturesLoading } = useQuery(
    PerformanceProcessAgreementDocumentsEsignaturesDocument,
    {
      variables: { documentId: Number(document?.id) },
      skip: !document?.id,
    }
  )
  const esignatures = esignaturesData?.esignature.signaturesConnection.nodes ?? []

  const statusAlert = useStatusAlert({ agreement: agreementWithDocuments ?? null, esignatures })

  useSubscription(AgreementRefreshedDocument, {
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.performanceAgreementRefresh?.agreement?.id !== agreementId) return

      refetch()
      refetchQuestionnaires()
      refetchDocuments()
    },
  })

  const transactionalActions = useBuildTransactionalActions({
    agreement,
    currentAccessId: currentAccess.id,
  })

  const signedByYou =
    agreement &&
    ((agreement.managerSignedAt && agreement.signer?.id == currentAccess.id) ||
      (agreement.targetSignedAt && agreement.target?.access?.id == currentAccess.id))

  const banner: ResponsivePageBanner =
    agreementWithDocuments?.status === 'signed'
      ? {
          message: i18n.t('performance_process.agreements.agreement.signed_by_both_parties'),
          icon: 'signature',
          color: 'info',
        }
      : agreement?.locked
      ? {
          message: signedByYou
            ? i18n.t('performance_process.agreements.agreement.locked_signed_by_you')
            : i18n.t('performance_process.agreements.agreement.locked_signed_by', {
                name: agreement.signer?.fullName || agreement.target?.access?.fullName,
              }),
          icon: 'lock-locked',
          color: 'warning',
        }
      : undefined

  return (
    <ResponsivePage.Body
      loading={loading || questionnairesLoading || documentsLoading}
      error={error || questionnairesError || documentsError}
      transactionalActions={isAgreementDocumentEnabled ? undefined : transactionalActions}
      banner={isAgreementDocumentEnabled ? undefined : banner}
    >
      {(statusAlert || document) && (
        <Box paddingBottom="s32" gap="s16">
          {statusAlert && <OneAlert {...statusAlert} />}
          {document && (
            <DocumentCard
              document={document}
              esignatures={esignatures}
              esignaturesLoading={esignaturesLoading}
            />
          )}
        </Box>
      )}

      {agreement && agreementWithQuestionnaires && (
        <Box gap="s24">
          <SectionableQuestions agreement={agreementWithQuestionnaires} />

          <Box gap="s24">
            {canSeeActionItems && (
              <>
                <Text.Header2 weight="semibold">
                  {i18n.t('performance_process.agreements.action_items.title')}
                </Text.Header2>
                <ActionItemList agreement={agreement} />
              </>
            )}

            <AgreementCourse agreement={agreement} HeaderComponent={Text.Header2} />

            {(!agreement.locked || agreement.conclusions) && (
              <>
                <Text.Header2 weight="semibold">
                  {i18n.t('performance_process.agreements.conclusions.title')}
                </Text.Header2>
                <AgreementConclusions agreement={agreement} />
              </>
            )}
          </Box>
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewAgreementPage
